import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError, maskDoc, parseMoney } from "@/lib/api";
import { cartFromItems } from "./useRepeatPurchase";
import { fetchHeldSales, discardHeldSale } from "./HeldSales";

/**
 * Vendas em aberto do PDV: guardar, listar, retomar e descartar.
 *
 * Vive fora da tela porque é um ciclo de vida próprio — o carrinho tem um
 * destino além de "virar venda", e as três operações conversam entre si (guardar
 * limpa o balcão, retomar tira da lista). Os setters do carrinho chegam de fora
 * porque quem é dono do estado da venda continua sendo o PDV.
 */
export default function useHeldSales({ cart, customer, manualDoc, discountType, discount, payments, resetSale, applyCart, onFocus }) {
  const [held, setHeld] = useState([]);
  const [heldOpen, setHeldOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [holding, setHolding] = useState(false);
  const [resumingId, setResumingId] = useState(null);

  useEffect(() => { fetchHeldSales().then(setHeld); }, []);

  const saveHeld = async (label) => {
    if (cart.length === 0 || holding) return;
    setHolding(true);
    try {
      await api.post("/pos/held-sales", {
        label: label || null,
        items: cart.map((i) => ({
          product_id: i.product_id, variant_id: i.variant_id, qty: i.qty,
          unit_price: i.price, discount: i.discount || 0,
          name: i.name, variant_label: i.variant_label,
        })),
        customer_id: customer?.id || null,
        customer_name: customer?.name || null,
        customer_document: customer?.document || (manualDoc ? manualDoc.replace(/\D/g, "") : null),
        discount_type: discountType,
        discount_value: parseMoney(discount),
        // O acerto combinado viaja junto: guardar a venda no meio da divisão
        // ("R$ 200 no PIX, o resto no crédito") e perder o rateio obrigava a
        // refazê-lo de cabeça quando o cliente voltasse.
        payments: (payments || []).map((p) => ({
          method: p.method,
          amount: parseMoney(p.amount),
          installments: p.method === "cartao_credito" ? parseInt(p.installments) || 1 : undefined,
        })),
      });
      setHeld(await fetchHeldSales());
      setSaveOpen(false);
      resetSale();
      toast.success("Venda guardada. O balcão está livre para o próximo cliente.");
      onFocus?.();
    } catch (e) { toast.error(apiError(e)); }
    finally { setHolding(false); }
  };

  /**
   * Retomar: o carrinho volta com os preços de AGORA, e o rascunho sai da lista.
   *
   * Sair da lista é o ponto delicado. Mantê-lo abriria a porta para o pior erro
   * possível aqui: retomar, vender, e o mesmo carrinho continuar disponível para
   * ser retomado e vendido de novo. Perder um rascunho é remontar o carrinho;
   * vender duas vezes é cobrar o cliente duas vezes.
   */
  const resumeHeld = async (h) => {
    if (resumingId) return;
    setResumingId(h.id);
    try {
      const { data } = await api.get(`/pos/held-sales/${h.id}`);
      const itens = cartFromItems(data.items);
      if (itens.length === 0) {
        toast.error("Os produtos desta venda não existem mais no catálogo.");
        return;
      }
      applyCart({
        cart: itens,
        discountType: data.discount_type || "valor",
        discount: data.discount_value ? String(data.discount_value) : "",
        customer: data.customer_id
          ? { id: data.customer_id, name: data.customer_name, document: data.customer_document }
          : null,
        manualDoc: !data.customer_id && data.customer_document ? maskDoc(data.customer_document) : "",
        // Volta como TEXTO no campo de valor, que é o formato que o PDV edita.
        payments: (data.payments || []).map((p) => ({
          method: p.method,
          amount: Number(p.amount || 0).toFixed(2),
          installments: p.installments ?? (p.method === "cartao_credito" ? 1 : null),
        })),
        warnings: data.items || [],
      });

      await api.delete(`/pos/held-sales/${h.id}`).catch(() => { /* já saiu da lista local */ });
      setHeld((prev) => prev.filter((x) => x.id !== h.id));
      setHeldOpen(false);
      toast.success("Venda retomada.");
      onFocus?.();
    } catch (e) { toast.error(apiError(e)); }
    finally { setResumingId(null); }
  };

  const removeHeld = async (h) => {
    if (await discardHeldSale(h.id)) {
      setHeld((prev) => prev.filter((x) => x.id !== h.id));
      toast.success("Venda em aberto descartada.");
    }
  };

  return {
    held, heldOpen, setHeldOpen, saveOpen, setSaveOpen,
    holding, resumingId, saveHeld, resumeHeld, removeHeld,
  };
}
